import { useAnalytics } from '@contexts/AnalyticsContext'
import {
  FetchMembershipAnalyticsGraphParams,
  MembershipAnalyticsGraphMonthlyRevenue,
  MembershipAnalyticsGraphMonthlyRevenueResponse,
} from '@models/membership/MembershipAnalyticsGraph'
import { MembershipAnalyticsCurrency } from '@models/membership/MembershipAnalyticsStatistics'
import { fetchAnalyticsGraph } from '@services/AnalyticsGraph'
import { analyticsCurrency } from '@utils/currencyFormatter'
import { Card, Empty, Flex, Spin, theme, Typography } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

const { Text } = Typography

const BAR_COLOR = '#1677ff'
const BAR_PARTIAL_COLOR = 'rgba(22,119,255,0.35)'
const LINE_COLOR = '#13c2c2'

interface Props {
  currency?: MembershipAnalyticsCurrency
}

const LegendItem = ({
  swatch,
  label,
}: {
  swatch: 'bar' | 'line'
  label: string
}) => (
  <Flex align="center" gap={6}>
    <span
      style={{
        display: 'inline-block',
        borderRadius: 2,
        width: swatch === 'bar' ? 10 : 14,
        height: swatch === 'bar' ? 10 : 3,
        background: swatch === 'bar' ? BAR_COLOR : LINE_COLOR,
      }}
    />
    <Text style={{ fontSize: 12 }}>{label}</Text>
  </Flex>
)

/**
 * "Revenue collected & active subscriptions, by month" (Figma 993:2158): bars =
 * collected, line = active subscriptions at month end, the current (partial)
 * month drawn lighter. Follows the date range; every value is the API's.
 */
const MonthlyRevenueChart = ({ currency: fallbackCurrency }: Props) => {
  const { token } = theme.useToken()
  const {
    selectedDates,
    selectedMembershipPrograms,
    isMembershipProgramReady,
  } = useAnalytics()

  const [data, setData] = useState<MembershipAnalyticsGraphMonthlyRevenue[]>([])
  // Undefined until the graph answers: then the statistics' currency stands in.
  const [graphCurrency, setGraphCurrency] = useState<
    MembershipAnalyticsCurrency | undefined
  >(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string>()
  const latest = useRef(0)

  useEffect(() => {
    if (!isMembershipProgramReady || !selectedDates) return

    const params: FetchMembershipAnalyticsGraphParams = {
      entity: 'membership',
      kind: 'membership-monthly-revenue',
      startDate: selectedDates[0],
      endDate: selectedDates[1],
      programIds: selectedMembershipPrograms,
    }

    const requestId = ++latest.current
    setIsLoading(true)
    setError(undefined)

    fetchAnalyticsGraph<MembershipAnalyticsGraphMonthlyRevenueResponse>(params)
      .then(({ data: response }) => {
        if (requestId !== latest.current) return
        // `{ currency, data: [...] }`, not a bare array.
        setData(response?.data ?? [])
        setGraphCurrency(response ? response.currency : undefined)
        if (!response) setError('No data available.')
      })
      .catch((e) => {
        if (requestId !== latest.current) return
        console.error('Error fetching data:', e)
        setError('An error occurred while fetching data.')
      })
      .finally(() => {
        if (requestId === latest.current) setIsLoading(false)
      })
  }, [selectedDates, selectedMembershipPrograms, isMembershipProgramReady])

  // The graph carries its own currency at the root of its response; the period
  // statistics' one is only the fallback until it has answered.
  const currency =
    graphCurrency !== undefined ? graphCurrency : fallbackCurrency

  // "5,9k €": thousands shorthand of the API's figure, symbol of its currency.
  const compactMoney = (cents?: number) => {
    if (typeof cents !== 'number') return ''
    const symbol = currency?.symbol || currency?.isoCode
    const thousands = cents / 100000
    const text =
      Math.abs(thousands) >= 1
        ? `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(thousands)}k`
        : analyticsCurrency(cents, false, null)
    return symbol ? `${text} ${symbol}` : text
  }

  const spansYears =
    new Set(data.map((point) => point.month.slice(0, 4))).size > 1
  const monthLabel = (month: string) =>
    dayjs(`${month}-01`).format(spansYears ? 'MMM YY' : 'MMM')

  return (
    <Card style={{ borderRadius: 8 }} styles={{ body: { padding: 20 } }}>
      <Flex vertical gap={12}>
        <Flex justify="space-between" align="center" wrap gap={12}>
          <Text style={{ fontSize: 14, color: token.colorTextTertiary }}>
            Revenue collected & active subscriptions, by month
          </Text>
          <Flex gap={16}>
            <LegendItem swatch="bar" label="Revenue collected" />
            <LegendItem
              swatch="line"
              label="Active subscriptions (end of month)"
            />
          </Flex>
        </Flex>
        {isLoading ? (
          <Flex align="center" justify="center" style={{ height: 260 }}>
            <Spin />
          </Flex>
        ) : error || data.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={error} />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={data} margin={{ top: 28, right: 8, left: 8 }}>
              <CartesianGrid
                vertical={false}
                stroke={token.colorBorderSecondary}
              />
              <XAxis
                dataKey="month"
                tickFormatter={monthLabel}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: token.colorTextTertiary }}
              />
              {/* Bars use the lower part, the line the upper one, so a bar label
                  never meets a line label. Scale only, no figure involved. */}
              <YAxis
                yAxisId="money"
                hide
                domain={[0, (max: number) => max * 1.9]}
              />
              <YAxis
                yAxisId="count"
                hide
                orientation="right"
                domain={[0, 'auto']}
              />
              <Tooltip
                labelFormatter={(month) => monthLabel(String(month))}
                formatter={(value, name) =>
                  name === 'collected'
                    ? [
                        typeof value === 'number'
                          ? analyticsCurrency(value, false, currency)
                          : '—',
                        'Revenue collected',
                      ]
                    : [value, 'Active subscriptions']
                }
              />
              <Bar
                yAxisId="money"
                dataKey="collected"
                name="collected"
                barSize={32}
                radius={[4, 4, 0, 0]}
              >
                {data.map((point) => (
                  <Cell
                    key={point.month}
                    fill={point.partial ? BAR_PARTIAL_COLOR : BAR_COLOR}
                  />
                ))}
                <LabelList
                  dataKey="collected"
                  content={(props: any) => {
                    const { x, y, width, value, index } = props
                    // Null = mixed currencies: no figure to print on the bar.
                    if (typeof value !== 'number') return null
                    // Above the bar, not inside it: a figure like "75,2k €" is
                    // wider than the bar, and a near-empty bar (current month)
                    // has no room at all.
                    return (
                      <text
                        x={Number(x) + Number(width) / 2}
                        y={Number(y) - 6}
                        textAnchor="middle"
                        fontSize={11}
                        fontWeight={600}
                        fill={
                          data[index]?.partial
                            ? token.colorTextTertiary
                            : token.colorText
                        }
                      >
                        {compactMoney(Number(value))}
                      </text>
                    )
                  }}
                />
              </Bar>
              <Line
                yAxisId="count"
                type="monotone"
                dataKey="activeSubscriptions"
                name="activeSubscriptions"
                stroke={LINE_COLOR}
                strokeWidth={2}
                dot={{ r: 3, fill: LINE_COLOR }}
              >
                <LabelList
                  dataKey="activeSubscriptions"
                  position="top"
                  offset={10}
                  formatter={(value: unknown) =>
                    new Intl.NumberFormat('fr-FR').format(Number(value))
                  }
                  style={{ fontSize: 11, fontWeight: 600, fill: LINE_COLOR }}
                />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        )}
        <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
          Revenue = completed payments in the month, after coupons. The current
          month is partial and shown lighter.
        </Text>
      </Flex>
    </Card>
  )
}

export default MonthlyRevenueChart
